import { useRef } from "react";
import Router from "next/router";
import { remiLogger } from "@remi/logging";
import { useEffect } from "@remi/next-ui";

import { api, apiVersion, queryClient } from "~/utils/api";
import { clearAllCache } from "~/utils/cache";
import { claimReloadGuard, VERSION_RELOAD_GUARD_KEY } from "~/utils/stale-build-reload";
import { displayToast } from "~/components/ui/sonner";

const VERSION_POLL_MS = 60_000; // balances deploy detection speed vs. server load
const MUTATION_RETRY_MS = 2_000;

export function useVersionSync() {
  const queryResult = api.config.getApiVersion.useQuery(undefined, {
    refetchInterval: VERSION_POLL_MS,
  });
  const serverVersion = queryResult.data?.apiVersion;

  // Initialized to the build-time apiVersion baked into the JS bundle.
  // If a deploy lands between page load and first poll, this intentionally
  // triggers a purge — the bundle is already stale at that point.
  const syncedVersionRef = useRef(apiVersion);

  useEffect(() => {
    if (!serverVersion || serverVersion === syncedVersionRef.current) {
      return;
    }

    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const attemptPurge = () => {
      if (queryClient.isMutating() > 0) {
        retryTimer = setTimeout(attemptPurge, MUTATION_RETRY_MS);
        return;
      }

      syncedVersionRef.current = serverVersion;

      Promise.all([
        clearAllCache({
          clearQueryClient: false,
          clearIndexedDb: true,
          clearLocalStorage: false,
          clearSessionStorage: false,
        }),
        queryClient.invalidateQueries(),
      ]).catch((error: unknown) => {
        remiLogger.error("versionSyncCachePurgeFailed", { error });
      });

      displayToast({
        title: "Updated — refresh for the latest code",
        duration: Infinity,
        closeButton: true,
      });
    };

    attemptPurge();
    return () => clearTimeout(retryTimer);
  }, [serverVersion]);

  // A tab that keeps this bundle mixes chunks from two builds on later navigations, which fails as "module
  // factory is not available". The guard stops a deploy whose HTML still serves this bundle from looping.
  useEffect(() => {
    if (!serverVersion || serverVersion === apiVersion) {
      return;
    }

    const reloadIntoNewBuild = (url: string, { shallow }: { shallow: boolean }) => {
      if (shallow || queryClient.isMutating() > 0) {
        return;
      }
      if (!claimReloadGuard(VERSION_RELOAD_GUARD_KEY, serverVersion)) {
        return;
      }
      window.location.assign(url);
    };

    Router.events.on("routeChangeStart", reloadIntoNewBuild);
    return () => Router.events.off("routeChangeStart", reloadIntoNewBuild);
  }, [serverVersion]);

  // Immediate version check when backgrounded tab resumes
  useEffect(() => {
    let lastCheck = 0;
    const handler = () => {
      const now = Date.now();
      if (document.visibilityState === "visible" && now - lastCheck > 30_000) {
        lastCheck = now;
        void queryClient.invalidateQueries({
          queryKey: [["config", "getApiVersion"]],
        });
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, []);

  return queryResult;
}
